import type {
  ApiService,
  ArtifactDest,
  ArtifactRelease,
  BetaSlot,
  Deployment,
  DevApi,
  DevEnv,
  DevResourcePack,
  MinecraftContainer,
  MinecraftInstance,
  Project,
  ResourcePack,
  User,
} from '@/deploy/types';
import { CONTAINER_COLORS, DEST_TO_KIND } from '@/deploy/types';
import raw from './containers.generated.json';

// ============================================================
// Fake data — basé sur la vraie prod Paladium Bedrock.
// Les containers et leurs release-version.json proviennent des
// vrais dépôts Paladium-Bedrock/PaladiumBE-*-Container.
// ============================================================

export const MOCK_USERS: User[] = [
  {
    id: 'u-admin',
    name: 'Maxence Aubry',
    email: 'maxence@paladium.games',
    role: 'admin',
    avatarColor: '#3B82F6',
    permissions: [
      'cicd.view',
      'cicd.deploy.beta',
      'cicd.deploy.preprod',
      'cicd.deploy.prod',
      'cicd.restart',
      'cicd.config.dotnet',
      'cicd.resourcepack.release',
      'cicd.schedule',
      'cicd.logs',
    ],
    maxDevEnvs: undefined, // illimité
  },
  {
    id: 'u-dev',
    name: 'Lucas Dev',
    email: 'lucas.dev@paladium.games',
    role: 'developer',
    avatarColor: '#A78BFA',
    permissions: ['cicd.view', 'cicd.deploy.beta', 'cicd.restart', 'cicd.logs'],
    maxDevEnvs: 3,
  },
  {
    id: 'u-pm',
    name: 'Sarah Project',
    email: 'sarah.pm@paladium.games',
    role: 'project-manager',
    avatarColor: '#38BDF8',
    permissions: [
      'cicd.view',
      'cicd.deploy.beta',
      'cicd.deploy.preprod',
      'cicd.schedule',
      'cicd.resourcepack.release',
      'cicd.logs',
    ],
    maxDevEnvs: 2,
  },
  {
    id: 'u-viewer',
    name: 'Invité',
    email: 'viewer@paladium.games',
    role: 'viewer',
    avatarColor: '#94A3B8',
    permissions: ['cicd.view', 'cicd.logs'],
    maxDevEnvs: 0,
  },
];

export const MOCK_PROJECTS: Project[] = [
  {
    id: 'p-recube',
    name: 'Paladium Bedrock',
    code: 'PBE',
    description: 'Serveurs Bedrock · Paladium — Recube',
    color: '#3B82F6',
    dotnetVersion: '9.0.304',
    createdAt: '2025-01-14T10:00:00Z',
  },
  {
    id: 'p-ngb',
    name: 'NG Bedrock',
    code: 'NGB',
    description: 'Nouveau moteur de jeu Bedrock (en préparation)',
    color: '#6366F1',
    dotnetVersion: '9.0.304',
    createdAt: '2026-02-18T09:30:00Z',
  },
];

// ---------- Containers Minecraft ----------

const MGMT: Record<string, 'individual' | 'nexus-dynamic'> = {
  build:    'individual',
  faction:  'individual',
  farmland: 'nexus-dynamic',
  lobby:    'nexus-dynamic',
  mining:   'nexus-dynamic',
  proxy:    'individual',
  warzone:  'nexus-dynamic',
};

// Versions déployées réalistes : dev = version du repo, autres en retard
function versionsFor(codeName: string, version: string) {
  const bump = (v: string, delta: number) => {
    const m = v.match(/^v?(\d+)\.(\d+)\.(\d+)$/);
    if (!m) return v;
    const patch = Math.max(0, parseInt(m[3], 10) - delta);
    return `v${m[1]}.${m[2]}.${patch}`;
  };
  return {
    dev:     version,
    beta:    bump(version, 1),
    preprod: bump(version, 3),
    prod:    bump(version, codeName === 'proxy' ? 1 : 4), // proxy suit de près
  };
}

function pendingFor(version: string, deployed: Record<string, string>) {
  const parse = (v: string) => parseInt(v.replace(/^v/, '').split('.').pop() || '0', 10);
  const ref = parse(version);
  return {
    dev: 0,
    beta: Math.max(0, ref - parse(deployed.beta)),
    preprod: Math.max(0, ref - parse(deployed.preprod)),
    prod: Math.max(0, ref - parse(deployed.prod)),
  };
}

type RawRelease = { name: string; repository: string; version: string; dest: string; assetName: string };
type RawContainer = { version: string; releases: RawRelease[] };
type Raw = Record<string, RawContainer>;

const rawContainers = raw as Raw;

export const MOCK_CONTAINERS: MinecraftContainer[] = Object.entries(rawContainers).map(([code, { version, releases }]) => {
  const deployed = versionsFor(code, version);
  const artifacts: ArtifactRelease[] = releases.map((r) => {
    const dest = (r.dest || '/mcserv/mods/') as ArtifactDest;
    const kind = DEST_TO_KIND[dest] ?? 'mod';
    // ~25% des artefacts ont une version plus récente disponible sur GitHub
    const lp = r.version.split('.');
    const patch = parseInt(lp[lp.length - 1] || '0', 10);
    const latestAvailable = Math.random() < 0.25
      ? `${lp.slice(0, -1).join('.')}.${patch + 1}`
      : undefined;
    return {
      name: r.name,
      repository: r.repository,
      version: r.version,
      dest,
      assetName: r.assetName,
      kind,
      latestAvailable,
    };
  });
  return {
    id: `c-${code}`,
    projectId: 'p-recube',
    code,
    displayName: code.charAt(0).toUpperCase() + code.slice(1),
    color: CONTAINER_COLORS[code] ?? '#3B82F6',
    repository: `Paladium-Bedrock/PaladiumBE-${code.charAt(0).toUpperCase() + code.slice(1)}-Container`,
    containerVersion: version,
    managementMode: MGMT[code] ?? 'individual',
    dotnetVersion: '9.0.304',
    releases: artifacts,
    lastBuildAt: new Date(Date.now() - Math.random() * 86400000 * 7).toISOString(),
    deployedVersions: deployed,
    pendingBumps: pendingFor(version, deployed),
  };
});

// ---------- Instances Minecraft (runtime) ----------

function inst(
  containerId: string,
  name: string,
  env: 'dev' | 'beta' | 'preprod' | 'prod',
  nexus: boolean,
  players: number | undefined,
  runningOffset: number,
  node: string,
  health: MinecraftInstance['health'] = 'healthy',
): MinecraftInstance {
  const container = MOCK_CONTAINERS.find((c) => c.id === containerId)!;
  const envVer = container.deployedVersions[env] ?? container.containerVersion;
  const bumpDown = (v: string | null, d: number) => {
    if (!v) return container.containerVersion;
    const m = v.match(/^v?(\d+)\.(\d+)\.(\d+)$/);
    if (!m) return v;
    return `v${m[1]}.${m[2]}.${Math.max(0, parseInt(m[3], 10) - d)}`;
  };
  return {
    id: `i-${name}`,
    projectId: 'p-recube',
    containerId,
    environment: env,
    name,
    runningVersion: bumpDown(envVer, runningOffset),
    health,
    uptimeSec: 60 * 60 * (2 + Math.floor(Math.random() * 120)),
    players,
    node,
    pteroServerId: Math.random().toString(16).slice(2, 10),
    isNexusManaged: nexus,
    lastHeartbeatAt: new Date(Date.now() - Math.random() * 60000).toISOString(),
  };
}

export const MOCK_INSTANCES: MinecraftInstance[] = [
  // --- PROD ---
  inst('c-proxy', 'proxy-prod-eu-1', 'prod', false, undefined, 0, 'prod-eu-1'),
  inst('c-proxy', 'proxy-prod-eu-2', 'prod', false, undefined, 0, 'prod-eu-2'),
  inst('c-proxy', 'proxy-prod-na-1', 'prod', false, undefined, 0, 'prod-na-1'),
  inst('c-faction', 'faction-pvp-1', 'prod', false, 342, 0, 'prod-eu-1'),
  inst('c-faction', 'faction-pvp-2', 'prod', false, 298, 1, 'prod-eu-1', 'degraded'),
  inst('c-faction', 'faction-pvp-3', 'prod', false, 189, 0, 'prod-eu-2'),
  inst('c-build', 'build-main', 'prod', false, 4, 0, 'prod-eu-1'),
  inst('c-lobby', 'lobby-dyn-01', 'prod', true, 128, 0, 'prod-eu-1'),
  inst('c-lobby', 'lobby-dyn-02', 'prod', true, 142, 0, 'prod-eu-1'),
  inst('c-lobby', 'lobby-dyn-03', 'prod', true, 99, 2, 'prod-eu-2'),
  inst('c-farmland', 'farmland-dyn-01', 'prod', true, 67, 0, 'prod-eu-1'),
  inst('c-farmland', 'farmland-dyn-02', 'prod', true, 54, 0, 'prod-eu-2'),
  inst('c-mining', 'mining-dyn-01', 'prod', true, 78, 0, 'prod-eu-1'),
  inst('c-mining', 'mining-dyn-02', 'prod', true, 41, 3, 'prod-eu-2'), // runtime en retard
  inst('c-warzone', 'warzone-dyn-01', 'prod', true, 86, 0, 'prod-eu-1'),
  inst('c-warzone', 'warzone-dyn-02', 'prod', true, 102, 0, 'prod-eu-2'),

  // --- PRÉ-PROD (copie exacte de la prod — version simplifiée) ---
  inst('c-proxy', 'proxy-preprod', 'preprod', false, undefined, 0, 'pp-eu-1'),
  inst('c-faction', 'faction-preprod', 'preprod', false, 3, 0, 'pp-eu-1'),
  inst('c-lobby', 'lobby-preprod', 'preprod', true, 2, 0, 'pp-eu-1'),
  inst('c-mining', 'mining-preprod', 'preprod', true, 1, 0, 'pp-eu-1'),
  inst('c-warzone', 'warzone-preprod', 'preprod', true, 0, 0, 'pp-eu-1'),

  // Bêta & Dev sont générés dynamiquement à partir des dev envs et beta slots
  // (voir MOCK_INSTANCES.push() plus bas).
];

// ---------- API services ----------
// Screenshot source :
//   2108120.ModerationAPI (Detached)
//   2107703.ProdileApi    (Detached)
//   378374.PalaProfile    (Detached)
//   4194275.LeaderboardApi(Detached)
//   1188935.StoreApi      (Detached)
//   934467.FactionApi     (Detached)
//   711900.StorePointApi  (Detached)
//   705960.MarketApi      (Detached)
//   683448.MailBoxApi     (Detached)
//   681630.VaultApi       (Detached)
//   477867.QueueApi       (Detached)
//   387101.Sync           (Detached)
//   385887.EssentialsAPI  (Detached)
//   380762.LuckPermAPI    (Detached)

const API_DEFS: Array<[string, string, string, ApiService['targetRuntime']]> = [
  ['ModerationAPI',   '2108120.ModerationAPI',   '2.4.1', 'pterodactyl'],
  ['ProfileApi',      '2107703.ProfileApi',      '3.1.0', 'pterodactyl'],
  ['PalaProfile',     '378374.PalaProfile',      '1.9.7', 'pterodactyl'],
  ['LeaderboardApi',  '4194275.LeaderboardApi',  '2.0.3', 'pterodactyl'],
  ['StoreApi',        '1188935.StoreApi',        '4.2.0', 'pterodactyl'],
  ['FactionApi',      '934467.FactionApi',       '2.6.4', 'pterodactyl'],
  ['StorePointApi',   '711900.StorePointApi',    '1.3.0', 'pterodactyl'],
  ['MarketApi',       '705960.MarketApi',        '2.1.1', 'pterodactyl'],
  ['MailBoxApi',      '683448.MailBoxApi',       '1.0.9', 'pterodactyl'],
  ['VaultApi',        '681630.VaultApi',         '1.4.0', 'pterodactyl'],
  ['QueueApi',        '477867.QueueApi',         '1.2.0', 'pterodactyl'],
  ['Sync',            '387101.Sync',             '2.0.0', 'pterodactyl'],
  ['EssentialsAPI',   '385887.EssentialsAPI',    '3.0.2', 'pterodactyl'],
  ['LuckPermAPI',     '380762.LuckPermAPI',      '1.7.1', 'pterodactyl'],
];

// APIs qui ont une release plus récente prête à être déployée
const API_LATEST_AVAILABLE: Record<string, string> = {
  ModerationAPI:  '2.4.2',
  StoreApi:       '4.3.0',
  FactionApi:     '2.6.5',
  MarketApi:      '2.1.2',
  LeaderboardApi: '2.0.4',
  Sync:           '2.1.0',
};

export const MOCK_APIS: ApiService[] = API_DEFS.map(([name, screen, ver, target], i) => ({
  id: `api-${name.toLowerCase()}`,
  projectId: 'p-recube',
  name,
  pidOrScreen: screen,
  runtime: 'screen', // aujourd'hui en screen — basculera en container pendant la phase de dev
  targetRuntime: target,
  health: i === 5 ? 'degraded' : 'healthy',
  version: ver,
  latestAvailable: API_LATEST_AVAILABLE[name],
  lastDeployAt: new Date(Date.now() - Math.random() * 86400000 * 180).toISOString(),
  environment: 'prod',
  node: 'legacy-vm-1',
  uptimeSec: 86400 * (30 + Math.floor(Math.random() * 150)),
}));

// ---------- Dev APIs ----------
// APIs en cours de développement, créables depuis le panel.
export const MOCK_DEV_APIS: DevApi[] = [
  {
    id: 'da-event',
    projectId: 'p-recube',
    name: 'EventAPI',
    description: 'API d\'events saisonniers — event Pâques',
    repository: 'Paladium-Bedrock/EventAPI',
    version: '0.3.2',
    startCommand: 'dotnet EventAPI.dll --config ./config/prod.json',
    dockerImage: 'paladium/event-api:dev',
    port: 9100,
    status: 'running',
    scope: 'env-specific',
    attachedEnvIds: ['de-sarah-mining'],
    createdAt: new Date(Date.now() - 86400000 * 6).toISOString(),
    createdBy: 'u-pm',
    createdByName: 'Sarah Project',
    createdByColor: '#38BDF8',
  },
  {
    id: 'da-metrics',
    projectId: 'p-recube',
    name: 'MetricsAPI',
    description: 'Centralise les métriques serveur — remplacera Prometheus custom',
    repository: 'Paladium-Bedrock/MetricsAPI',
    version: '0.1.4',
    startCommand: 'dotnet MetricsAPI.dll',
    dockerImage: 'paladium/metrics-api:dev',
    port: 9200,
    status: 'running',
    scope: 'shared',
    attachedEnvIds: [],
    createdAt: new Date(Date.now() - 86400000 * 14).toISOString(),
    createdBy: 'u-dev',
    createdByName: 'Lucas Dev',
    createdByColor: '#A78BFA',
  },
  {
    id: 'da-boss',
    projectId: 'p-recube',
    name: 'BossFightAPI',
    description: 'Nouvelle API pour orchestrer les combats de boss Warzone',
    repository: 'Paladium-Bedrock/BossFightAPI',
    version: '0.0.8',
    startCommand: 'dotnet BossFightAPI.dll',
    port: 9301,
    status: 'building',
    scope: 'env-specific',
    attachedEnvIds: ['de-lucas-warzone'],
    createdAt: new Date(Date.now() - 3600000 * 8).toISOString(),
    createdBy: 'u-dev',
    createdByName: 'Lucas Dev',
    createdByColor: '#A78BFA',
  },
  {
    id: 'da-friends',
    projectId: 'p-recube',
    name: 'FriendsAPI',
    description: 'Gestion du système d\'amis cross-server',
    repository: 'Paladium-Bedrock/FriendsAPI',
    version: '0.2.0',
    startCommand: 'dotnet FriendsAPI.dll',
    dockerImage: 'paladium/friends-api:dev',
    port: 9400,
    status: 'running',
    scope: 'shared',
    attachedEnvIds: [],
    createdAt: new Date(Date.now() - 86400000 * 21).toISOString(),
    createdBy: 'u-dev',
    createdByName: 'Lucas Dev',
    createdByColor: '#A78BFA',
  },
  {
    id: 'da-guild',
    projectId: 'p-recube',
    name: 'GuildAPI',
    description: 'Refonte du système de guildes (remplacement de FactionApi v3)',
    repository: 'Paladium-Bedrock/GuildAPI',
    version: '0.0.3',
    startCommand: 'dotnet GuildAPI.dll',
    port: 9500,
    status: 'stopped',
    scope: 'shared',
    attachedEnvIds: [],
    createdAt: new Date(Date.now() - 86400000 * 4).toISOString(),
    createdBy: 'u-admin',
    createdByName: 'Maxence Aubry',
    createdByColor: '#3B82F6',
  },
];

// ---------- Dev environments ----------
// Chaque env de dev = 1 seul service MC (choix du model) + APIs dev attachées.

export const MOCK_DEV_ENVS: DevEnv[] = [
  {
    id: 'de-lucas-faction',
    projectId: 'p-recube',
    name: 'lucas-faction-economy',
    ownerId: 'u-dev',
    ownerName: 'Lucas Dev',
    ownerColor: '#A78BFA',
    modelCode: 'faction',
    createdAt: new Date(Date.now() - 86400000 * 2).toISOString(),
    apiMode: 'shared',
    customApis: [],
    attachedDevApiIds: ['da-metrics', 'da-friends'],
    mcInstanceId: 'i-faction-dev',
    status: 'running',
  },
  {
    id: 'de-sarah-mining',
    projectId: 'p-recube',
    name: 'sarah-mining-event-spring',
    ownerId: 'u-pm',
    ownerName: 'Sarah Project',
    ownerColor: '#38BDF8',
    modelCode: 'mining',
    createdAt: new Date(Date.now() - 86400000 * 5).toISOString(),
    apiMode: 'isolated',
    customApis: [],
    attachedDevApiIds: ['da-event', 'da-metrics'],
    mcInstanceId: 'i-mining-dev-sarah',
    status: 'running',
  },
  {
    id: 'de-lucas-warzone',
    projectId: 'p-recube',
    name: 'lucas-warzone-boss',
    ownerId: 'u-dev',
    ownerName: 'Lucas Dev',
    ownerColor: '#A78BFA',
    modelCode: 'warzone',
    createdAt: new Date(Date.now() - 3600000 * 6).toISOString(),
    apiMode: 'shared',
    customApis: [],
    attachedDevApiIds: ['da-boss'],
    mcInstanceId: 'i-warzone-dev',
    status: 'stopped',
  },
  {
    id: 'de-admin-lobby',
    projectId: 'p-recube',
    name: 'maxence-lobby-rework',
    ownerId: 'u-admin',
    ownerName: 'Maxence Aubry',
    ownerColor: '#3B82F6',
    modelCode: 'lobby',
    createdAt: new Date(Date.now() - 3600000 * 3).toISOString(),
    apiMode: 'shared',
    customApis: [],
    attachedDevApiIds: ['da-metrics'],
    mcInstanceId: 'i-lobby-dev-admin',
    status: 'running',
  },
  {
    id: 'de-lucas-mining',
    projectId: 'p-recube',
    name: 'lucas-mining-refactor',
    ownerId: 'u-dev',
    ownerName: 'Lucas Dev',
    ownerColor: '#A78BFA',
    modelCode: 'mining',
    createdAt: new Date(Date.now() - 86400000 * 1).toISOString(),
    apiMode: 'isolated',
    customApis: [],
    attachedDevApiIds: ['da-metrics', 'da-guild'],
    mcInstanceId: 'i-mining-dev-lucas',
    status: 'running',
  },
  {
    id: 'de-sarah-build',
    projectId: 'p-recube',
    name: 'sarah-build-event-map',
    ownerId: 'u-pm',
    ownerName: 'Sarah Project',
    ownerColor: '#38BDF8',
    modelCode: 'build',
    createdAt: new Date(Date.now() - 3600000 * 12).toISOString(),
    apiMode: 'shared',
    customApis: [],
    attachedDevApiIds: [],
    mcInstanceId: 'i-build-dev-sarah',
    status: 'running',
  },
];

// On ajoute aussi les instances MC des env de dev listés
MOCK_INSTANCES.push(
  {
    id: 'i-faction-dev',
    projectId: 'p-recube',
    containerId: 'c-faction',
    environment: 'dev',
    name: 'faction-lucas',
    runningVersion: 'v0.0.122',
    health: 'healthy',
    uptimeSec: 3600 * 18,
    players: 2,
    node: 'dev-shared-1',
    pteroServerId: 'dev-fac-a',
    isNexusManaged: false,
    lastHeartbeatAt: new Date().toISOString(),
  },
  {
    id: 'i-mining-dev-sarah',
    projectId: 'p-recube',
    containerId: 'c-mining',
    environment: 'dev',
    name: 'mining-sarah',
    runningVersion: 'v0.0.106',
    health: 'healthy',
    uptimeSec: 3600 * 48,
    players: 1,
    node: 'dev-shared-2',
    pteroServerId: 'dev-min-a',
    isNexusManaged: false,
    lastHeartbeatAt: new Date().toISOString(),
  },
  {
    id: 'i-warzone-dev',
    projectId: 'p-recube',
    containerId: 'c-warzone',
    environment: 'dev',
    name: 'warzone-lucas',
    runningVersion: 'v0.0.22',
    health: 'down',
    uptimeSec: 0,
    players: 0,
    node: 'dev-shared-1',
    pteroServerId: 'dev-wz-a',
    isNexusManaged: false,
    lastHeartbeatAt: new Date(Date.now() - 3600000).toISOString(),
  },
  {
    id: 'i-lobby-dev-admin',
    projectId: 'p-recube',
    containerId: 'c-lobby',
    environment: 'dev',
    name: 'lobby-maxence',
    runningVersion: 'v0.0.141',
    health: 'healthy',
    uptimeSec: 3600 * 3,
    players: 0,
    node: 'dev-shared-2',
    pteroServerId: 'dev-lob-a',
    isNexusManaged: false,
    lastHeartbeatAt: new Date().toISOString(),
  },
  {
    id: 'i-mining-dev-lucas',
    projectId: 'p-recube',
    containerId: 'c-mining',
    environment: 'dev',
    name: 'mining-lucas-refact',
    runningVersion: 'v0.0.107',
    health: 'healthy',
    uptimeSec: 86400,
    players: 3,
    node: 'dev-shared-1',
    pteroServerId: 'dev-min-b',
    isNexusManaged: false,
    lastHeartbeatAt: new Date().toISOString(),
  },
  {
    id: 'i-build-dev-sarah',
    projectId: 'p-recube',
    containerId: 'c-build',
    environment: 'dev',
    name: 'build-sarah-map',
    runningVersion: 'v0.0.26',
    health: 'healthy',
    uptimeSec: 3600 * 12,
    players: 1,
    node: 'dev-shared-2',
    pteroServerId: 'dev-bld-a',
    isNexusManaged: false,
    lastHeartbeatAt: new Date().toISOString(),
  },
);

// ---------- Beta slots A..E ----------

export const MOCK_BETA_SLOTS: BetaSlot[] = [
  {
    id: 'A',
    projectId: 'p-recube',
    allocated: true,
    label: 'Patch économie Faction',
    sourceDevEnvId: 'de-lucas-faction',
    sourceDevEnvName: 'lucas-faction-economy',
    allocatedAt: new Date(Date.now() - 86400000).toISOString(),
    allocatedByUserId: 'u-dev',
    allocatedByUserName: 'Lucas Dev',
    mcInstanceId: 'i-faction-beta-A',
    modelCode: 'faction',
    // Snapshot: au push, le dev tournait sur v0.0.121 avec seulement da-metrics.
    // Depuis, Lucas a bumpé le container à v0.0.122 et ajouté da-friends → diff visible.
    snapshotContainerVersion: 'v0.0.121',
    snapshotApiIds: ['da-metrics'],
    snapshotAt: new Date(Date.now() - 86400000).toISOString(),
  },
  {
    id: 'B',
    projectId: 'p-recube',
    allocated: true,
    label: 'Event Pâques Mining',
    sourceDevEnvId: 'de-sarah-mining',
    sourceDevEnvName: 'sarah-mining-event-spring',
    allocatedAt: new Date(Date.now() - 3600000 * 8).toISOString(),
    allocatedByUserId: 'u-pm',
    allocatedByUserName: 'Sarah Project',
    mcInstanceId: 'i-mining-beta-B',
    modelCode: 'mining',
    // Snapshot: au push, Sarah avait da-event + da-metrics + da-guild.
    // Depuis, elle a retiré da-guild → diff visible (-1 API).
    snapshotContainerVersion: 'v0.0.105',
    snapshotApiIds: ['da-event', 'da-metrics', 'da-guild'],
    snapshotAt: new Date(Date.now() - 3600000 * 8).toISOString(),
  },
  { id: 'C', projectId: 'p-recube', allocated: false },
  { id: 'D', projectId: 'p-recube', allocated: false },
  {
    id: 'E',
    projectId: 'p-recube',
    allocated: true,
    label: 'Test nouveau boss Warzone',
    sourceDevEnvName: 'arnaud-warzone-boss',
    allocatedAt: new Date(Date.now() - 3600000 * 36).toISOString(),
    allocatedByUserName: 'Arnaud Play',
    mcInstanceId: 'i-warzone-beta-E',
    modelCode: 'warzone',
    snapshotContainerVersion: 'v0.0.21',
    snapshotApiIds: [],
    snapshotAt: new Date(Date.now() - 3600000 * 36).toISOString(),
  },
];

// Instances MC associées aux beta slots alloués
MOCK_INSTANCES.push(
  {
    id: 'i-faction-beta-A',
    projectId: 'p-recube',
    containerId: 'c-faction',
    environment: 'beta',
    name: 'beta-A · faction-economy',
    runningVersion: 'v0.0.121',
    health: 'healthy',
    uptimeSec: 86400,
    players: 18,
    node: 'beta-eu-1',
    pteroServerId: 'beta-a',
    isNexusManaged: false,
    lastHeartbeatAt: new Date().toISOString(),
  },
  {
    id: 'i-mining-beta-B',
    projectId: 'p-recube',
    containerId: 'c-mining',
    environment: 'beta',
    name: 'beta-B · mining-event',
    runningVersion: 'v0.0.105',
    health: 'healthy',
    uptimeSec: 3600 * 30,
    players: 11,
    node: 'beta-eu-1',
    pteroServerId: 'beta-b',
    isNexusManaged: false,
    lastHeartbeatAt: new Date().toISOString(),
  },
  {
    id: 'i-warzone-beta-E',
    projectId: 'p-recube',
    containerId: 'c-warzone',
    environment: 'beta',
    name: 'beta-E · warzone-boss',
    runningVersion: 'v0.0.21',
    health: 'degraded',
    uptimeSec: 3600 * 40,
    players: 6,
    node: 'beta-eu-1',
    pteroServerId: 'beta-e',
    isNexusManaged: false,
    lastHeartbeatAt: new Date().toISOString(),
  },
);

// ---------- Resource packs (prod) ----------
// Basé sur la vraie liste : Hub, Pala, PalaUi, Premium, Warzone.
export const MOCK_RESOURCE_PACKS: ResourcePack[] = [
  {
    id: 'rp-hub',
    projectId: 'p-recube',
    name: 'Hub',
    fileName: 'HubResourcePack.zip',
    description: 'Pack visuel du hub principal · accueil joueurs',
    repository: 'Paladium-Bedrock/HubResourcePack',
    currentVersion: '1.0.8',
    latestAvailable: '1.1.0',
    activeIn: ['prod', 'preprod', 'beta'],
    lastUpdateAt: new Date(Date.now() - 86400000 * 122).toISOString(), // 4 mois
    lastUpdateBy: 'marie.art',
    commitMessage: 'Upgrade all',
    sizeMb: 8.4,
  },
  {
    id: 'rp-pala',
    projectId: 'p-recube',
    name: 'Pala',
    fileName: 'PalaResourcePack.zip',
    description: 'Pack principal · textures gameplay & entités',
    repository: 'Paladium-Bedrock/PalaResourcePack',
    currentVersion: '2.4.0',
    latestAvailable: '2.4.1',
    activeIn: ['prod', 'preprod'],
    lastUpdateAt: new Date(Date.now() - 86400000 * 14).toISOString(),
    lastUpdateBy: 'marie.art',
    commitMessage: 'Update PalaResourcePack.zip with new version details',
    sizeMb: 42.1,
  },
  {
    id: 'rp-pala-ui',
    projectId: 'p-recube',
    name: 'PalaUi',
    fileName: 'PalaUiResourcePack.zip',
    description: 'Interface utilisateur · HUD · menus · icônes',
    repository: 'Paladium-Bedrock/PalaUiResourcePack',
    currentVersion: '3.1.2',
    activeIn: ['prod', 'preprod', 'beta'],
    lastUpdateAt: new Date(Date.now() - 86400000 * 7).toISOString(),
    lastUpdateBy: 'marie.art',
    commitMessage: 'Update resource pack zip files with new version details',
    sizeMb: 3.8,
  },
  {
    id: 'rp-premium',
    projectId: 'p-recube',
    name: 'Premium',
    fileName: 'PremiumResourcePack.zip',
    description: 'Textures exclusives joueurs premium · skins & capes',
    repository: 'Paladium-Bedrock/PremiumResourcePack',
    currentVersion: '1.3.5',
    latestAvailable: '1.4.0',
    activeIn: ['prod', 'preprod'],
    lastUpdateAt: new Date(Date.now() - 86400000 * 6).toISOString(),
    lastUpdateBy: 'marie.art',
    commitMessage: 'Update PremiumResourcePack.zip with new version details',
    sizeMb: 12.7,
  },
  {
    id: 'rp-warzone',
    projectId: 'p-recube',
    name: 'Warzone',
    fileName: 'WarzoneResourcePack.zip',
    description: 'Pack dédié au mode Warzone · armes & interfaces PvP',
    repository: 'Paladium-Bedrock/WarzoneResourcePack',
    currentVersion: '0.9.3',
    activeIn: ['prod', 'preprod'],
    lastUpdateAt: new Date(Date.now() - 86400000 * 21).toISOString(),
    lastUpdateBy: 'marie.art',
    commitMessage: 'Initial release',
    sizeMb: 18.2,
  },
];

// ---------- Resource packs (dev) ----------
export const MOCK_DEV_RESOURCE_PACKS: DevResourcePack[] = [
  {
    id: 'drp-spring-event',
    projectId: 'p-recube',
    name: 'SpringEvent',
    fileName: 'SpringEventResourcePack.zip',
    description: 'Pack thématique Pâques · textures saisonnières (event)',
    repository: 'Paladium-Bedrock/SpringEventResourcePack',
    version: '0.3.0',
    status: 'ready',
    scope: 'env-specific',
    attachedEnvIds: ['de-sarah-mining'],
    createdAt: new Date(Date.now() - 86400000 * 8).toISOString(),
    createdBy: 'u-pm',
    createdByName: 'Sarah Project',
    createdByColor: '#38BDF8',
    sizeMb: 6.5,
  },
  {
    id: 'drp-ngb-ui',
    projectId: 'p-recube',
    name: 'NGBUi',
    fileName: 'NGBUiResourcePack.zip',
    description: 'Nouvelle UI pour NG Bedrock · prototype en cours',
    repository: 'Paladium-Bedrock/NGBUiResourcePack',
    version: '0.1.2',
    status: 'building',
    scope: 'shared',
    attachedEnvIds: [],
    createdAt: new Date(Date.now() - 86400000 * 18).toISOString(),
    createdBy: 'u-dev',
    createdByName: 'Lucas Dev',
    createdByColor: '#A78BFA',
    sizeMb: 2.1,
  },
  {
    id: 'drp-xmas',
    projectId: 'p-recube',
    name: 'ChristmasRework',
    fileName: 'ChristmasResourcePack.zip',
    description: 'Refonte du pack de Noël · à activer en décembre',
    repository: 'Paladium-Bedrock/ChristmasResourcePack',
    version: '0.0.5',
    status: 'ready',
    scope: 'shared',
    attachedEnvIds: [],
    createdAt: new Date(Date.now() - 86400000 * 32).toISOString(),
    createdBy: 'u-admin',
    createdByName: 'Maxence Aubry',
    createdByColor: '#3B82F6',
    sizeMb: 9.3,
  },
];

// ---------- Historique de déploiements ----------
// ---------- Historique des déploiements (scénarios réalistes) ----------
// Les `items` sont encodés "id@version" ou "rp:id@version" ; la page /history
// les résout en labels lisibles via MOCK_CONTAINERS / MOCK_APIS / MOCK_RESOURCE_PACKS.
export const MOCK_DEPLOYMENTS: Deployment[] = [
  // -- Dernière promotion Pré-prod → Prod réussie --
  {
    id: 'd-010',
    projectId: 'p-recube',
    source: 'preprod',
    target: 'prod',
    kind: 'container',
    items: ['c-faction@v0.0.118', 'c-lobby@v0.0.136', 'c-mining@v0.0.102', 'api-moderationapi@2.4.1', 'rp:rp-pala-ui@3.1.2'],
    includedServices: ['prod'],
    status: 'success',
    author: MOCK_USERS[0],
    startedAt: new Date(Date.now() - 3600000 * 3).toISOString(),
    finishedAt: new Date(Date.now() - 3600000 * 3 + 92000).toISOString(),
    durationSec: 92,
    restartAfter: true,
    note: 'Promotion Pré-prod → Prod · release hebdomadaire',
    steps: [
      { id: 'st-1', label: 'Vérification santé Pré-prod', status: 'success' },
      { id: 'st-2', label: 'Promotion images vers Prod', status: 'success' },
      { id: 'st-3', label: 'Sync versions APIs', status: 'success' },
      { id: 'st-4', label: 'Redémarrage par vague Prod', status: 'success' },
    ],
  },
  // -- Build Pré-prod from scratch récent --
  {
    id: 'd-009',
    projectId: 'p-recube',
    source: 'dev',
    target: 'preprod',
    kind: 'container',
    items: [
      'c-faction@v0.0.122',
      'c-lobby@v0.0.140',
      'c-mining@v0.0.106',
      'api-moderationapi@2.4.2',
      'api-storeapi@4.3.0',
      'rp:rp-pala@2.4.1',
      'rp:rp-premium@1.4.0',
    ],
    includedServices: ['preprod'],
    status: 'success',
    author: MOCK_USERS[0],
    startedAt: new Date(Date.now() - 3600000 * 6).toISOString(),
    finishedAt: new Date(Date.now() - 3600000 * 6 + 147000).toISOString(),
    durationSec: 147,
    restartAfter: true,
    note: 'Build Pré-prod avec maj APIs et packs',
    steps: [
      { id: 'st-1', label: 'Bump release-version.json', status: 'success' },
      { id: 'st-2', label: 'Build des images Docker', status: 'success' },
      { id: 'st-3', label: 'Push sur registry', status: 'success' },
      { id: 'st-4', label: 'Mise à jour eggs Pterodactyl', status: 'success' },
      { id: 'st-5', label: 'Redémarrage instances Pré-prod', status: 'success' },
    ],
  },
  // -- Déploiement ciblé d'une API en Pré-prod --
  {
    id: 'd-008',
    projectId: 'p-recube',
    source: 'dev',
    target: 'preprod',
    kind: 'api',
    items: ['api-leaderboardapi@2.0.4'],
    includedServices: ['preprod'],
    status: 'success',
    author: MOCK_USERS[1],
    startedAt: new Date(Date.now() - 3600000 * 14).toISOString(),
    finishedAt: new Date(Date.now() - 3600000 * 14 + 28000).toISOString(),
    durationSec: 28,
    restartAfter: true,
    note: 'Hotfix Leaderboard · cache régression',
    steps: [
      { id: 'st-1', label: 'Build API', status: 'success' },
      { id: 'st-2', label: 'Push container', status: 'success' },
      { id: 'st-3', label: 'Déploiement en Pré-prod', status: 'success' },
      { id: 'st-4', label: 'Redémarrage API', status: 'success' },
    ],
  },
  // -- Update d'un slot Bêta --
  {
    id: 'd-007',
    projectId: 'p-recube',
    source: 'dev',
    target: 'beta',
    kind: 'container',
    items: ['c-faction@v0.0.122', 'api-moderationapi@2.4.2'],
    includedServices: ['beta'],
    status: 'success',
    author: MOCK_USERS[1],
    startedAt: new Date(Date.now() - 3600000 * 20).toISOString(),
    finishedAt: new Date(Date.now() - 3600000 * 20 + 38000).toISOString(),
    durationSec: 38,
    restartAfter: true,
    note: 'Update Bêta A · re-push lucas-faction-economy (v0.0.121 → v0.0.122, + FriendsAPI)',
    steps: [
      { id: 'st-1', label: 'Calcul du diff', status: 'success' },
      { id: 'st-2', label: 'Application du delta container', status: 'success' },
      { id: 'st-3', label: 'Sync APIs (ajout/retrait)', status: 'success' },
      { id: 'st-4', label: 'Redémarrage du service MC', status: 'success' },
    ],
  },
  // -- Push d'un env de dev vers un slot Bêta --
  {
    id: 'd-006',
    projectId: 'p-recube',
    source: 'dev',
    target: 'beta',
    kind: 'container',
    items: ['c-mining@v0.0.105'],
    includedServices: ['beta'],
    status: 'success',
    author: MOCK_USERS[2],
    startedAt: new Date(Date.now() - 3600000 * 30).toISOString(),
    finishedAt: new Date(Date.now() - 3600000 * 30 + 55000).toISOString(),
    durationSec: 55,
    restartAfter: true,
    note: 'Allocation Bêta B · push sarah-mining-event-spring (event Pâques)',
    steps: [
      { id: 'st-1', label: 'Snapshot de l\'env de dev', status: 'success' },
      { id: 'st-2', label: 'Allocation du slot Bêta B', status: 'success' },
      { id: 'st-3', label: 'Spawn instance MC', status: 'success' },
      { id: 'st-4', label: 'Déploiement APIs attachées', status: 'success' },
    ],
  },
  // -- Déploiement RP ciblé --
  {
    id: 'd-005',
    projectId: 'p-recube',
    source: 'dev',
    target: 'preprod',
    kind: 'resource-pack',
    items: ['rp:rp-pala-ui@3.1.2'],
    includedServices: ['preprod'],
    status: 'success',
    author: MOCK_USERS[2],
    startedAt: new Date(Date.now() - 86400000 * 2).toISOString(),
    finishedAt: new Date(Date.now() - 86400000 * 2 + 18000).toISOString(),
    durationSec: 18,
    restartAfter: false,
    note: 'Update rapide du PalaUi',
    steps: [
      { id: 'st-1', label: 'Packaging .zip', status: 'success' },
      { id: 'st-2', label: 'Upload sur CDN', status: 'success' },
      { id: 'st-3', label: 'Activation côté clients', status: 'success' },
    ],
  },
  // -- Échec (rollback auto) --
  {
    id: 'd-004',
    projectId: 'p-recube',
    source: 'preprod',
    target: 'prod',
    kind: 'container',
    items: ['c-proxy@v0.0.95', 'c-faction@v0.0.117'],
    includedServices: ['prod'],
    status: 'failed',
    author: MOCK_USERS[0],
    startedAt: new Date(Date.now() - 86400000 * 3).toISOString(),
    finishedAt: new Date(Date.now() - 86400000 * 3 + 41000).toISOString(),
    durationSec: 41,
    restartAfter: true,
    note: 'Rollback automatique · proxy v0.0.95 incompatible avec PalaMod 0.0.33',
    steps: [
      { id: 'st-1', label: 'Vérification santé Pré-prod', status: 'success' },
      { id: 'st-2', label: 'Promotion images vers Prod', status: 'success' },
      { id: 'st-3', label: 'Sync versions APIs', status: 'failed' },
      { id: 'st-4', label: 'Rollback automatique', status: 'success' },
    ],
  },
  // -- Push Bêta plus ancien (Arnaud externe) --
  {
    id: 'd-003',
    projectId: 'p-recube',
    source: 'dev',
    target: 'beta',
    kind: 'container',
    items: ['c-warzone@v0.0.21'],
    includedServices: ['beta'],
    status: 'success',
    author: MOCK_USERS[1],
    startedAt: new Date(Date.now() - 86400000 * 4).toISOString(),
    finishedAt: new Date(Date.now() - 86400000 * 4 + 60000).toISOString(),
    durationSec: 60,
    restartAfter: true,
    note: 'Allocation Bêta E · arnaud-warzone-boss · test nouveau boss',
    steps: [
      { id: 'st-1', label: 'Snapshot de l\'env de dev', status: 'success' },
      { id: 'st-2', label: 'Allocation du slot Bêta E', status: 'success' },
      { id: 'st-3', label: 'Spawn instance MC', status: 'success' },
      { id: 'st-4', label: 'Déploiement APIs attachées', status: 'success' },
    ],
  },
  // -- Release semaine précédente --
  {
    id: 'd-002',
    projectId: 'p-recube',
    source: 'preprod',
    target: 'prod',
    kind: 'container',
    items: ['c-lobby@v0.0.134', 'c-farmland@v0.0.73', 'rp:rp-hub@1.0.8'],
    includedServices: ['prod'],
    status: 'success',
    author: MOCK_USERS[0],
    startedAt: new Date(Date.now() - 86400000 * 7).toISOString(),
    finishedAt: new Date(Date.now() - 86400000 * 7 + 104000).toISOString(),
    durationSec: 104,
    restartAfter: true,
    note: 'Release mensuelle · refonte hub + ajustement farmland',
    steps: [
      { id: 'st-1', label: 'Vérification santé Pré-prod', status: 'success' },
      { id: 'st-2', label: 'Promotion images vers Prod', status: 'success' },
      { id: 'st-3', label: 'Sync versions APIs', status: 'success' },
      { id: 'st-4', label: 'Redémarrage par vague Prod', status: 'success' },
    ],
  },
  // -- Création d'une API dev ancienne --
  {
    id: 'd-001',
    projectId: 'p-recube',
    source: 'dev',
    target: 'dev',
    kind: 'api',
    items: ['api-metricsapi@0.1.0'],
    includedServices: ['dev'],
    status: 'success',
    author: MOCK_USERS[1],
    startedAt: new Date(Date.now() - 86400000 * 14).toISOString(),
    finishedAt: new Date(Date.now() - 86400000 * 14 + 22000).toISOString(),
    durationSec: 22,
    restartAfter: false,
    note: 'Création MetricsAPI en dev partagé',
    steps: [
      { id: 'st-1', label: 'Clone repo GitHub', status: 'success' },
      { id: 'st-2', label: 'Build container', status: 'success' },
      { id: 'st-3', label: 'Démarrage initial', status: 'success' },
    ],
  },
];
